// Tidy Board auto-layout for TouchTour Studio (WP-I).
//
// `tidyBoard` returns fresh { x, y } grid coordinates for every node on the
// board. Layout follows the *direction of each link hotspot* on the source node:
// a link that exits the right edge places its target to the right (same row), a
// link that exits the bottom places its target below (same column), and so on.
// That keeps connector lines short and straight instead of forcing everything
// into a centred tree.
//
// Hotspot → edge mapping mirrors TourCanvasGrid.getXYfromOffsetAndRatio.
//
// Usage:
//   const positions = tidyBoard(tour.images, { startImageId: tour.data.startImageId });

export const BOARD_LIMIT = 200;
export const NODE_GRID_W = 10;
export const NODE_GRID_H = 10;
export const COLUMN_GAP = 6;
export const ROW_GAP = 6;

const LINK_HOTSPOT_TYPES = ["link", "fade", "elevate-up", "elevate-down"];

const clampToBoard = value => {
  if (value > BOARD_LIMIT) return BOARD_LIMIT;
  if (value < -BOARD_LIMIT) return -BOARD_LIMIT;
  return value;
};

const toImageArray = images => {
  if (Array.isArray(images)) return images.filter(Boolean);
  if (images && typeof images === "object") return Object.values(images);
  return [];
};

const numberOr = (value, fallback) =>
  typeof value === "number" && Number.isFinite(value) ? value : fallback;

const compareReading = (a, b) => {
  const ay = numberOr(a.y, 0);
  const by = numberOr(b.y, 0);
  if (ay !== by) return by - ay;
  const ax = numberOr(a.x, 0);
  const bx = numberOr(b.x, 0);
  if (ax !== bx) return ax - bx;
  return String(a.id).localeCompare(String(b.id));
};

// Which edge of the node thumbnail the hotspot sits on (0–1 perimeter ratio).
const normalizeRatio = ratio => {
  let r = numberOr(ratio, 0.5);
  while (r < 0) r += 1;
  while (r > 1) r -= 1;
  return r;
};

export const ratioToDirection = ratio => {
  const r = normalizeRatio(ratio);
  if (r < 0.125) return "top";
  if (r < 0.375) return "right";
  if (r < 0.625) return "bottom";
  if (r < 0.875) return "left";
  return "top";
};

// Grid delta for placing the target node one step away in `dir`.
// Board y points up; screen-bottom links step y down (negative delta).
export const directionOffset = (dir, colStep, rowStep) => {
  switch (dir) {
    case "right":
      return { dx: colStep, dy: 0 };
    case "left":
      return { dx: -colStep, dy: 0 };
    case "bottom":
      return { dx: 0, dy: -rowStep };
    case "top":
      return { dx: 0, dy: rowStep };
    default:
      return { dx: 0, dy: -rowStep };
  }
};

const occupies = (positions, x, y) =>
  Object.values(positions).some(
    pos => Math.abs(pos.x - x) < NODE_GRID_W && Math.abs(pos.y - y) < NODE_GRID_H,
  );

// Build outgoing edges (with hotspot ratios) and undirected adjacency.
const buildGraph = nodes => {
  const ids = new Set(nodes.map(n => String(n.id)));
  const outgoing = new Map();
  const undirected = new Map();
  const indegree = new Map();

  ids.forEach(id => {
    outgoing.set(id, []);
    undirected.set(id, []);
    indegree.set(id, 0);
  });

  nodes.forEach(node => {
    const sid = String(node.id);
    (node.hotspots ?? []).forEach(hotspot => {
      if (!LINK_HOTSPOT_TYPES.includes(hotspot?.type)) return;
      const targetId =
        hotspot?.options?.id != null ? String(hotspot.options.id) : null;
      if (targetId == null || targetId === sid || !ids.has(targetId)) return;

      outgoing.get(sid).push({
        targetId,
        sourceRatio: hotspot.ratio,
      });
      undirected.get(sid).push(targetId);
      undirected.get(targetId).push(sid);
      indegree.set(targetId, indegree.get(targetId) + 1);
    });
  });

  return { outgoing, undirected, indegree };
};

const findComponents = (readingOrder, undirected) => {
  const seen = new Set();
  const components = [];

  readingOrder.forEach(node => {
    const sid = String(node.id);
    if (seen.has(sid)) return;

    const component = [];
    const stack = [sid];
    seen.add(sid);
    while (stack.length > 0) {
      const current = stack.pop();
      component.push(current);
      undirected.get(current).forEach(neighbour => {
        if (!seen.has(neighbour)) {
          seen.add(neighbour);
          stack.push(neighbour);
        }
      });
    }
    components.push(component);
  });

  return components;
};

// Walk outgoing links from `rootId`, placing each target one step in the
// direction its source hotspot exits. If that slot is taken, keep stepping
// further in the same direction until free.
const layoutFromRoot = (
  rootId,
  memberSet,
  outgoing,
  orderIndex,
  rootX,
  rootY,
  colStep,
  rowStep,
  globalPositions,
) => {
  const local = {};
  const byOrder = (a, b) => orderIndex.get(a) - orderIndex.get(b);

  local[rootId] = { x: clampToBoard(rootX), y: clampToBoard(rootY) };

  const queue = [rootId];
  const placed = new Set([rootId]);

  for (let i = 0; i < queue.length; i++) {
    const parentId = queue[i];
    const parentPos = local[parentId];

    const edges = [...outgoing.get(parentId)]
      .filter(edge => memberSet.has(edge.targetId))
      .sort((a, b) => orderIndex.get(a.targetId) - orderIndex.get(b.targetId));

    edges.forEach(({ targetId, sourceRatio }) => {
      if (placed.has(targetId)) return;

      const dir = ratioToDirection(sourceRatio);
      const { dx, dy } = directionOffset(dir, colStep, rowStep);

      let x = parentPos.x + dx;
      let y = parentPos.y + dy;
      let steps = 0;

      while (
        (occupies(local, x, y) || occupies(globalPositions, x, y)) &&
        steps < 50
      ) {
        x += dx;
        y += dy;
        steps += 1;
      }

      local[targetId] = { x: clampToBoard(x), y: clampToBoard(y) };
      placed.add(targetId);
      queue.push(targetId);
    });
  }

  return local;
};

const pickRoot = (component, indegree, orderIndex, startImageId) => {
  const memberSet = new Set(component);
  if (startImageId != null && memberSet.has(String(startImageId))) {
    return String(startImageId);
  }
  const roots = [...component]
    .filter(id => indegree.get(id) === 0)
    .sort((a, b) => orderIndex.get(a) - orderIndex.get(b));
  if (roots.length > 0) return roots[0];
  return [...component].sort((a, b) => orderIndex.get(a) - orderIndex.get(b))[0];
};

/**
 * @param {Object} [options]
 * @param {string} [options.startImageId] - prefer this node as the layout root.
 */
export const tidyBoard = (images, options = {}) => {
  const nodes = toImageArray(images).filter(img => img != null && img.id != null);
  if (nodes.length === 0) return {};

  const startX = numberOr(options.startX, -BOARD_LIMIT);
  const startY = numberOr(options.startY, BOARD_LIMIT);
  const colStep = NODE_GRID_W + Math.max(0, numberOr(options.columnGap, COLUMN_GAP));
  const rowStep = NODE_GRID_H + Math.max(0, numberOr(options.rowGap, ROW_GAP));
  const startImageId =
    options.startImageId != null ? String(options.startImageId) : null;

  const readingOrder = [...nodes].sort(compareReading);
  const orderIndex = new Map(
    readingOrder.map((node, index) => [String(node.id), index]),
  );

  const { outgoing, undirected, indegree } = buildGraph(nodes);
  const components = findComponents(readingOrder, undirected);

  const positions = {};
  let anchorX = startX;

  // Linked components: follow hotspot directions from the root outward.
  components
    .filter(component => component.length > 1)
    .forEach(component => {
      const memberSet = new Set(component);
      const rootId = pickRoot(component, indegree, orderIndex, startImageId);

      const local = layoutFromRoot(
        rootId,
        memberSet,
        outgoing,
        orderIndex,
        anchorX,
        startY,
        colStep,
        rowStep,
        positions,
      );

      // Any node not reached by BFS (back-links only) grid-pack beside the tree.
      const unplaced = [...component]
        .filter(id => local[id] == null)
        .sort((a, b) => orderIndex.get(a) - orderIndex.get(b));

      let sideX = anchorX;
      unplaced.forEach(id => {
        let x = sideX;
        let y = startY;
        while (occupies({ ...positions, ...local }, x, y)) {
          x += colStep;
        }
        local[id] = { x: clampToBoard(x), y: clampToBoard(y) };
        sideX = x + colStep;
      });

      Object.assign(positions, local);

      const maxX = Math.max(...Object.values(local).map(p => p.x));
      anchorX = maxX + colStep + COLUMN_GAP;
    });

  // Isolated scenes grid-pack after the linked blocks.
  const isolated = components
    .filter(component => component.length === 1)
    .map(component => component[0])
    .sort((a, b) => orderIndex.get(a) - orderIndex.get(b));

  if (isolated.length > 0) {
    const fitColumns = Math.max(
      1,
      Math.floor((BOARD_LIMIT - anchorX) / colStep) + 1,
    );
    const columns = Math.max(
      1,
      Math.floor(numberOr(options.columns, fitColumns)),
    );

    isolated.forEach((id, index) => {
      const column = index % columns;
      const row = Math.floor(index / columns);
      positions[id] = {
        x: clampToBoard(anchorX + column * colStep),
        y: clampToBoard(startY - row * rowStep),
      };
    });
  }

  return positions;
};

export default tidyBoard;
